import { defineStore } from 'pinia';
import { computed, markRaw, reactive, ref, shallowRef } from 'vue';

import { Map, View } from 'ol';
import API from '@/store/api/map';
import {
  CurrentDynamicLayer,
  FieldPhoto,
  LayerGroupInterface,
  LayerInterface,
  SliderType
} from '@/store/models/map';
import type { WmsLayerKey } from '@/mixins/WmsLayerSpecs';

import { createDefaultView } from '@/utils/ol-map';

// Setup-style store (not options-style). Pinia options-style infers state types
// through UnwrapRef, which decomposes OL class instances (Feature/View/Map have
// public `_`-suffixed fields and class identity matters). Setup-style refs
// preserve types exactly. OL instances use shallowRef + markRaw — they should
// never be deeply reactive, and they need to keep their class identity.
export const useMapStore = defineStore('map', () => {
  const mapState = shallowRef<Map>(markRaw(new Map({})));
  const sharedView = shallowRef<View>(
    markRaw(
      createDefaultView()
    )
  );

  let updateMapSizeFrame = 0;
  // Fullscreen photo viewer state. The set is every field photo of the active
  // photo layer for the current farm / crop / date filter, ordered by capture
  // date -- so < > walks on past the clicked point into photos taken at other
  // locations (FMW-490), not just the ones stacked at that point.
  const selectedPhotos = ref<FieldPhoto[]>([]);
  const selectedPhotoIndex = ref(0);
  // Current photo (null when the viewer is closed).
  const selectedPhoto = computed<FieldPhoto | null>(
    () => selectedPhotos.value[selectedPhotoIndex.value] ?? null
  );
  // Empty until the API has answered: nothing may build a layer against a
  // placeholder host, so map setup awaits getGeoserverConfig() first.
  const geoserverBaseURL = ref('');
  let geoserverConfigRequest: Promise<void> | undefined;
  const sliderType = ref<SliderType>(SliderType.PERIOD_OF_TIME);
  const fruitLayerVisibile = ref(false);
  const currentDynamicLayer = ref<CurrentDynamicLayer>({
    current: null,
    type: 'Flower',
    display: 'dots'
  });
  // Photo layer is controlled independently of currentDynamicLayer so it can be
  // shown alongside a dots layer. `type` is the active tab's photo layer
  // (DynamicLayerName.*_PHOTO); empty when the active tab has no photo layer.
  const currentPhotoLayer = ref<{ visible: boolean; type: string }>({
    visible: false,
    type: ''
  });
  const timerValue = ref(0);
  const timerRangeValue = ref<{ min: number; max: number }>({ min: 0, max: 1 });
  const BaseLayers = ref<LayerInterface[]>([]);
  const Layers = ref<(LayerInterface | LayerGroupInterface)[]>([]);
  const wmsLegends = reactive<Partial<Record<WmsLayerKey, string>>>({});
  const vectorLayerSetupComplete = ref(false);

  function setMap(map: Map) {
    mapState.value = markRaw(map);
  }
  function setVectorLayerSetupComplete(value: boolean) {
    vectorLayerSetupComplete.value = value;
  }
  // Per key, so an unchanged URL triggers nothing and a map leaves the
  // legends of layers it does not have alone.
  function setWmsLegends(urls: Partial<Record<WmsLayerKey, string>>) {
    Object.assign(wmsLegends, urls);
  }
  function setTimerValue(value: number) {
    timerValue.value = value;
  }
  function setTimerRangeValue(value: { min: number; max: number }) {
    timerRangeValue.value = value;
  }
  function setFruitLayerVisibile(visible: boolean) {
    fruitLayerVisibile.value = visible;
  }
  // Open the fullscreen viewer on photos[index]; pass [] to close it.
  function setSelectedPhotos(photos: FieldPhoto[], index = 0) {
    selectedPhotos.value = photos;
    selectedPhotoIndex.value = index;
  }
  // Move within the open set, clamped -- the viewer's < > and the popup
  // gallery's < > drive the same index, so they never disagree.
  function setSelectedPhotoIndex(index: number) {
    const lastIndex = selectedPhotos.value.length - 1;
    if (lastIndex < 0) return;
    selectedPhotoIndex.value = Math.min(Math.max(index, 0), lastIndex);
  }
  function setCurrentDynamicLayer(value: CurrentDynamicLayer) {
    currentDynamicLayer.value = value;
  }
  // Whether the dry matter layers colour by the reading grown to the day
  // looked at, rather than as measured (FMW-622).
  const dryMatterGrown = ref(false);
  function setDryMatterGrown(grown: boolean) {
    dryMatterGrown.value = grown;
  }
  // Each Side-by-Side map's tab, by side, kept across the page remounting on
  // a farm change as the main map's tab is.
  const compareTabs = ref<Record<string, string>>({});
  function setCompareTab(side: string, tab: string) {
    compareTabs.value = { ...compareTabs.value, [side]: tab };
  }
  // Whether the Side-by-Side maps' Layers panels are open: one state for
  // both, since they are adjusted and read together.
  const compareLayerPanel = ref(false);
  function setCompareLayerPanel(open: boolean) {
    compareLayerPanel.value = open;
  }
  function setCurrentPhotoLayer(value: { visible: boolean; type: string }) {
    currentPhotoLayer.value = value;
  }
  function setSliderType(value: SliderType.PERIOD_OF_TIME | SliderType.POINT_OF_TIME) {
    sliderType.value = value;
  }
  async function getProxiedFeatureInfo(url: string) {
    return API.proxyView(url);
  }
  function updateMapSize() {
    mapState.value.updateSize();
    updateMapSizeFrame = requestAnimationFrame(() => updateMapSize());
  }
  async function getBlockFeatureInfo(params: {
    farm: number;
    latitude: number;
    longitude: number;
  }) {
    return await API.getBlockFeature(
      params.farm,
      params.latitude,
      params.longitude
    );
  }
  function stopUpdateAnimation() {
    cancelAnimationFrame(updateMapSizeFrame);
    updateMapSizeFrame = 0;
  }
  function setOlControlDarkMode(darkMode: boolean) {
    const olControls = document.querySelectorAll(
      '.ol-control:not(.ol-attribution)'
    );
    if (darkMode) {
      for (const control of olControls) {
        control.classList.add('ol-control--dark');
      }
    } else {
      for (const control of olControls) {
        control.classList.remove('ol-control--dark');
      }
    }
  }
  async function getBaseLayers() {
    const query = `?layer_type=base_layer`;
    BaseLayers.value = await API.getLayers(query);
  }
  async function getLayers() {
    const layers = await API.getGroupedLayers('');
    Layers.value = layers.filter(x => x.layerType != 'base_layer');
  }
  // One request shared by every caller; a failed one is dropped so the next
  // caller retries instead of settling for an empty host.
  function getGeoserverConfig(): Promise<void> {
    if (!geoserverConfigRequest) {
      geoserverConfigRequest = API.getGeoserverConfig().then(
        config => {
          geoserverBaseURL.value = config.baseUrl;
        },
        error => {
          geoserverConfigRequest = undefined;
          throw error;
        }
      );
    }
    return geoserverConfigRequest;
  }

  return {
    mapState,
    sharedView,
    selectedPhoto,
    selectedPhotos,
    selectedPhotoIndex,
    geoserverBaseURL,
    sliderType,
    fruitLayerVisibile,
    currentDynamicLayer,
    currentPhotoLayer,
    timerValue,
    timerRangeValue,
    BaseLayers,
    Layers,
    wmsLegends,
    vectorLayerSetupComplete,
    setMap,
    setVectorLayerSetupComplete,
    setWmsLegends,
    setTimerValue,
    setTimerRangeValue,
    setFruitLayerVisibile,
    setSelectedPhotos,
    setSelectedPhotoIndex,
    setCurrentDynamicLayer,
    dryMatterGrown,
    setDryMatterGrown,
    compareTabs,
    setCompareTab,
    compareLayerPanel,
    setCompareLayerPanel,
    setCurrentPhotoLayer,
    setSliderType,
    getProxiedFeatureInfo,
    updateMapSize,
    getBlockFeatureInfo,
    stopUpdateAnimation,
    setOlControlDarkMode,
    getBaseLayers,
    getLayers,
    getGeoserverConfig
  };
});

